import { useEffect, useState } from "react";
import api, { formatApiError } from "../../lib/api";
import { Card } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Select, SelectTrigger, SelectContent, SelectItem, SelectValue } from "../../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import { Badge } from "../../components/ui/badge";
import { RefreshCw, ExternalLink, Plus, Trash2, Sparkles, BookOpen, Wand2 } from "lucide-react";
import { toast } from "sonner";
import { refsForSection } from "./referenceLibrary";
import { useAuth } from "../../context/AuthContext";
import { kipLink } from "../../lib/kipLink";
import PublishInsight from "./PublishInsight";

const TABS = [
  { id: "audit", label: "Audit & Accounting" },
  { id: "mca", label: "MCA / Company Law" },
  { id: "income_tax", label: "Income Tax" },
  { id: "gst", label: "GST" },
  { id: "rbi_fema", label: "RBI & FEMA" },
  { id: "sebi", label: "SEBI / Markets" },
  { id: "valuation", label: "Valuation" },
  { id: "insolvency", label: "IBC" },
  { id: "judicial", label: "Judicial" },
  { id: "financial_news", label: "Financial News" },
  { id: "others", label: "Others" },
];

const critColors = { high: "bg-red-100 text-red-700 border-red-200", medium: "bg-amber-100 text-amber-700 border-amber-200", low: "bg-emerald-100 text-emerald-700 border-emerald-200" };

export default function KipUpdates() {
  const { user } = useAuth();
  const isPartner = ["partner", "admin"].includes(user?.role);
  const canManage = ["partner", "admin", "manager"].includes(user?.role);
  const [denied, setDenied] = useState(false);
  const [tab, setTab] = useState("audit");
  const [crit, setCrit] = useState("");
  const [q, setQ] = useState("");
  const [items, setItems] = useState([]);
  const [showNew, setShowNew] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [seeding, setSeeding] = useState(false);
  const [summarising, setSummarising] = useState({});

  const load = async () => {
    const params = { tab };
    if (crit) params.criticality = crit;
    if (q) params.q = q;
    try {
      const { data } = await api.get("/kip/updates", { params });
      setItems(data);
      setDenied(false);
    } catch (e) {
      setItems([]);
      if (e?.response?.status === 403) setDenied(true);
      else toast.error(formatApiError(e));
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [tab, crit]);

  const summarise = async (uid, force = false) => {
    setSummarising((s) => ({ ...s, [uid]: true }));
    try {
      const { data } = await api.post(`/kip/updates/${uid}/summarise${force ? "?force=true" : ""}`);
      setItems((prev) => prev.map((x) => x.id === uid ? { ...x, ai_summary: data.summary } : x));
      toast.success(data.cached ? "Summary loaded from cache" : "AI summary generated");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSummarising((s) => ({ ...s, [uid]: false })); }
  };

  const seed = async () => {
    setSeeding(true);
    try { const { data } = await api.post("/kip/seed-samples"); toast.success(`Seeded ${data.inserted} samples`); load(); } 
    catch (e) { toast.error(formatApiError(e)); }
    finally { setSeeding(false); }
  };
  const refresh = async (allTabs = false) => {
    setRefreshing(true);
    try {
      const params = allTabs ? {} : { tab };
      const { data } = await api.post("/kip/refresh-feeds", null, { params });
      const breakdown = data.per_tab ? Object.entries(data.per_tab).map(([k, v]) => `${k}:${v}`).join(", ") : "";
      toast.success(`Fetched ${data.inserted} updates${breakdown ? ` (${breakdown})` : ""}`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setRefreshing(false); }
  };

  return (
    <div className="space-y-6" data-testid="kip-updates-page">
      <div className="flex items-center justify-between">
        <div>
          <div className="eyebrow">Knowledge Intelligence</div>
          <h1 className="font-sans text-3xl">Regulatory Updates</h1>
        </div>
        <div className="flex gap-2">
          {isPartner && <Button variant="outline" onClick={seed} disabled={seeding} data-testid="seed-samples-btn"><Sparkles className="h-4 w-4 mr-1"/>{seeding ? "Seeding..." : "Seed Samples"}</Button>}
          {canManage && <Button variant="outline" onClick={() => refresh(false)} disabled={refreshing} data-testid="refresh-feeds-btn"><RefreshCw className={`h-4 w-4 mr-1 ${refreshing ? "animate-spin" : ""}`}/>{refreshing ? "Refreshing..." : "Refresh Current Tab"}</Button>}
          {canManage && <Button onClick={() => refresh(true)} disabled={refreshing} data-testid="refresh-all-btn"><RefreshCw className={`h-4 w-4 mr-1 ${refreshing ? "animate-spin" : ""}`}/>Refresh All Regulators</Button>}
          {canManage && <Button onClick={() => setShowNew(true)} data-testid="new-update-btn"><Plus className="h-4 w-4 mr-1"/>New</Button>}
        </div>
      </div>

      <div className="flex gap-2 flex-wrap" data-testid="kip-tabs">
        {TABS.map((t) => (
          <button key={t.id} onClick={() => setTab(t.id)}
            className={`px-3 py-1.5 rounded-full text-xs border ${tab === t.id ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-700 border-slate-200 hover:border-slate-400"}`}
            data-testid={`tab-${t.id}`}>
            {t.label}
          </button>
        ))}
      </div>

      <div className="flex gap-3">
        <Input placeholder="Search title or source…" value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && load()} className="max-w-sm"/>
        <Select value={crit || "_all"} onValueChange={(v) => setCrit(v === "_all" ? "" : v)}>
          <SelectTrigger className="w-40"><SelectValue placeholder="Criticality"/></SelectTrigger>
          <SelectContent>
            <SelectItem value="_all">All criticality</SelectItem>
            <SelectItem value="high">High</SelectItem>
            <SelectItem value="medium">Medium</SelectItem>
            <SelectItem value="low">Low</SelectItem>
          </SelectContent>
        </Select>
        <Button onClick={load}>Apply</Button>
      </div>

      <div className="grid gap-3">
        {items.map((u) => (
          <Card key={u.id} className="p-4" data-testid={`update-${u.id}`}>
            <div className="flex items-start justify-between gap-4">
              <div className="flex-1">
                <div className="flex items-center gap-2 flex-wrap mb-1">
                  <Badge className={critColors[u.criticality]}>{u.criticality}</Badge>
                  <span className="text-xs text-slate-500">{u.source} · {u.issued_on}</span>
                  {u.category && <span className="text-xs text-slate-500">· {u.category}</span>}
                </div>
                <div className="font-semibold text-slate-900">{u.title}</div>
                {u.summary && <p className="text-sm text-slate-700 mt-1">{u.summary}</p>}
                {u.ai_summary && (
                  <div className="mt-2 p-2 bg-violet-50 border border-violet-200 rounded" data-testid={`ai-sum-${u.id}`}>
                    <div className="text-[10px] uppercase tracking-wider text-violet-700 mb-1 inline-flex items-center gap-1"><Wand2 className="h-3 w-3"/>AI summary</div>
                    <div className="text-sm text-slate-800 whitespace-pre-wrap">{u.ai_summary.replace(/\*\*/g, "").replace(/^#+\s*/gm, "").replace(/©/g, "(c)")}</div>
                  </div>
                )}
                <div className="flex items-center gap-3 mt-2">
                  {kipLink(u) && <a href={kipLink(u).href} target="_blank" rel="noopener noreferrer" className={`text-xs hover:underline inline-flex items-center gap-1 ${kipLink(u).dead ? "text-amber-700" : "text-blue-600"}`} data-testid={`kip-src-${u.id}`}>{kipLink(u).label} <ExternalLink className="h-3 w-3"/></a>}
                  {isPartner && !u.ai_summary && (
                    <button onClick={() => summarise(u.id)} disabled={summarising[u.id]} className="text-xs text-violet-700 hover:underline inline-flex items-center gap-1" data-testid={`summarise-${u.id}`}>
                      <Wand2 className={`h-3 w-3 ${summarising[u.id] ? "animate-pulse" : ""}`}/>{summarising[u.id] ? "Summarising…" : "Summarise (AI)"}
                    </button>
                  )}
                  {isPartner && u.ai_summary && (
                    <button onClick={() => summarise(u.id, true)} className="text-xs text-slate-500 hover:underline inline-flex items-center gap-1" data-testid={`resummarise-${u.id}`}>
                      <Wand2 className="h-3 w-3"/>Re-summarise
                    </button>
                  )}
                  {isPartner && <PublishInsight update={u} onPublished={(id, pid) => setItems((xs) => xs.map((x) => (x.id === id ? { ...x, published_insight_id: pid } : x)))} />}
                </div>
              </div>
              {isPartner && <Button variant="ghost" size="sm" onClick={async () => { if (window.confirm("Delete?")) { await api.delete(`/kip/updates/${u.id}`); load(); } }} data-testid={`kip-delete-${u.id}`}><Trash2 className="h-3 w-3"/></Button>}
            </div>
          </Card>
        ))}
        {!items.length && <Card className="p-8 text-center text-slate-400" data-testid="kip-empty">{denied ? "You don't have access to Regulatory Updates. Ask a partner to enable KIP for your account." : "No updates yet."}</Card>}
      </div>

      <SectionReferences_disabled section={tab}/>

      {showNew && <NewUpdateDialog tab={tab} onClose={() => setShowNew(false)} onSaved={() => { setShowNew(false); load(); }}/>}
    </div>
  );
}

function SectionReferences_disabled() { return null; }

function SectionReferences({ section }) {
  const refs = refsForSection(section);
  if (!refs.length) return null;
  return (
    <Card className="p-4 bg-amber-50/40 border-amber-200" data-testid="section-references">
      <div className="flex items-center gap-2 mb-3">
        <BookOpen className="h-4 w-4 text-[#C2A671]"/>
        <h2 className="font-semibold text-slate-900">Reference Library for this Section</h2>
      </div>
      <p className="text-xs text-slate-600 mb-3">Direct links to the latest Acts, Rules &amp; Regulations from official sources.</p>
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
        {refs.map((r) => (
          <a key={r.id} href={r.url} target="_blank" rel="noopener noreferrer"
            className="border border-amber-200 rounded p-2 text-sm bg-white hover:border-amber-400 hover:shadow-sm transition-all"
            data-testid={`ref-card-${r.id}`}>
            <div className="font-medium text-slate-900 text-xs line-clamp-2 leading-tight">{r.label}</div>
            <div className="text-[10px] text-slate-500 mt-1 inline-flex items-center gap-1">{r.source} <ExternalLink className="h-3 w-3"/></div>
          </a>
        ))}
      </div>
    </Card>
  );
}

function NewUpdateDialog({ tab, onClose, onSaved }) {
  const [form, setForm] = useState({
    tab, title: "", summary: "", source: "", source_url: "", issued_on: new Date().toISOString().slice(0, 10),
    criticality: "medium", category: "", body: "", tags: "",
  });
  const save = async () => {
    if (!form.title.trim()) { toast.error("Title required"); return; }
    try {
      await api.post("/kip/updates", { ...form, tags: form.tags ? form.tags.split(",").map(t => t.trim()) : [] });
      toast.success("Update added");
      onSaved();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl" data-testid="new-update-dialog">
        <DialogHeader><DialogTitle>New Regulatory Update</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <div><label className="text-xs text-slate-500">Tab</label>
            <Select value={form.tab} onValueChange={(v) => setForm({...form, tab: v})}>
              <SelectTrigger><SelectValue/></SelectTrigger>
              <SelectContent>{TABS.map(t => <SelectItem key={t.id} value={t.id}>{t.label}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><label className="text-xs text-slate-500">Criticality</label>
            <Select value={form.criticality} onValueChange={(v) => setForm({...form, criticality: v})}>
              <SelectTrigger><SelectValue/></SelectTrigger>
              <SelectContent><SelectItem value="high">High</SelectItem><SelectItem value="medium">Medium</SelectItem><SelectItem value="low">Low</SelectItem></SelectContent>
            </Select>
          </div>
          <div className="col-span-2"><label className="text-xs text-slate-500">Title *</label><Input value={form.title} onChange={(e) => setForm({...form, title: e.target.value})} data-testid="upd-title"/></div>
          <div className="col-span-2"><label className="text-xs text-slate-500">Summary</label><Textarea value={form.summary} onChange={(e) => setForm({...form, summary: e.target.value})}/></div>
          <div><label className="text-xs text-slate-500">Source</label><Input value={form.source} onChange={(e) => setForm({...form, source: e.target.value})} placeholder="RBI / CBDT / MCA"/></div>
          <div><label className="text-xs text-slate-500">Source URL</label><Input value={form.source_url} onChange={(e) => setForm({...form, source_url: e.target.value})}/></div>
          <div><label className="text-xs text-slate-500">Issued On</label><Input type="date" value={form.issued_on} onChange={(e) => setForm({...form, issued_on: e.target.value})}/></div>
          <div><label className="text-xs text-slate-500">Category</label><Input value={form.category} onChange={(e) => setForm({...form, category: e.target.value})}/></div>
          <div className="col-span-2"><label className="text-xs text-slate-500">Tags</label><Input value={form.tags} onChange={(e) => setForm({...form, tags: e.target.value})} placeholder="comma-separated"/></div>
        </div>
        <DialogFooter><Button variant="outline" onClick={onClose}>Cancel</Button><Button onClick={save} data-testid="upd-save">Save</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
